import React, { useState } from 'react';
import ProductRowActions from './ProductRowActions';
import DataFallback from '../feedback/DataFallback';
import './ManageProducts.css';

function ProductListTable({
  products,
  onProductAction,
  onRowClick,
  onProductSelect,
  onSelectAll,
  selectedProducts = [],
  selectAllMode = false,
  excludedIds = [],
  isReadOnly,
  userRole,
  isLoading,
  // A failed load, so the table can say so instead of claiming the catalogue is
  // empty. Optional: callers that do not pass it keep the previous behaviour.
  error = null,
  onRetry,
  userId,
  showBulkActions = false,
  rowActionsComponent: RowActionsComponent = ProductRowActions
}) {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked);
    } else {
      console.warn('onSelectAll prop is undefined!');
    }
  };
  
  // Handle individual product selection
  const handleProductSelect = (productId, isSelected) => {
    if (onProductSelect) {
      onProductSelect(productId, isSelected);
    } else {
      console.warn('onProductSelect prop is undefined!');
    }
  };
  
  // Handle bulk actions
  const handleBulkAction = (action) => {
    if (selectedProducts.length === 0) {
      alert('Please select products first.');
      return;
    }
    
    if (isReadOnly) {
      alert('You do not have permission to perform this action.');
      return;
    }
    
    switch (action) {
      case 'activate':
        console.log('Bulk activate products:', selectedProducts);
        break;
      case 'deactivate':
        console.log('Bulk deactivate products:', selectedProducts);
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
          console.log('Bulk delete products:', selectedProducts);
        }
        break;
      default:
        console.log('Unknown bulk action:', action);
    }
  };
  
  // Get product state dot color (green = active on Shopify)
  const getStateDotClass = (state) => {
    return state === 'active' ? 'stateDotActive' : 'stateDotInactive';
  };

  // Get product status badge class
  // Catalog status badge class — covers the v2 terminal_status vocabulary.
  const getProductStatusClass = (productStatus) => {
    switch (productStatus) {
      case 'active':   return 'statusLive';
      case 'draft':    return 'statusDraft';
      case 'archived': return 'statusInactive';
      case 'unlisted': return 'statusInactive';
      case 'deleted':  return 'statusDeleted';
      case 'inactive': return 'statusInactive'; // legacy fallback
      default:         return 'statusDefault';
    }
  };
  
  // Format currency
  const formatCurrency = (price) => {
    // Use Indian Rupee formatting
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(price);
  };
  
  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format an ISO timestamp as "12 Mar 2025, 4:05 PM" — the list shows date *and*
  // time so the team can tell how long ago a product was sent for approval.
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getPrimaryImageUrl = (product) => {
    const mediaItems = Array.isArray(product?.media) ? product.media : [];
    if (mediaItems.length === 0) return '';
    const primaryByFlag = mediaItems.find((item) => item?.is_primary);
    if (primaryByFlag?.url) return primaryByFlag.url;
    const withPosition = mediaItems.filter((item) => item?.position !== undefined && item?.position !== null);
    if (withPosition.length > 0) {
      const sorted = [...withPosition].sort((a, b) => Number(a.position) - Number(b.position));
      if (sorted[0]?.url) return sorted[0].url;
    }
    return mediaItems[0]?.url || '';
  };

  // Resolve a product's store mappings.
  //
  // The v2 product API exposes `store_mappings` — one entry per store the product
  // is present on (TerminalProductStoreMapping). Each entry has { platform,
  // owner_type, external_product_id, external_status }. A merchant entry means the
  // product is live on Suspire's Shopify store; vendor entries are the vendor's own
  // connected stores. A product is mapped iff it has at least one such mapping.
  //
  // Some callers hand us a whitelisted row object rather than the raw API product,
  // so fall back to the untouched payload kept on `_apiProduct`.
  const getStoreMappings = (product) => {
    const mappings = Array.isArray(product.store_mappings)
      ? product.store_mappings
      : Array.isArray(product._apiProduct?.store_mappings)
        ? product._apiProduct.store_mappings
        : null;
    return mappings;
  };

  // Vendor-store mappings only.
  //
  // Every product carries a merchant (Suspire Shopify) mapping as well, and that
  // row is never removed by unlinking or unmapping a vendor store — it also keeps
  // is_mapped=true. Linking and mapping are questions about the VENDOR store, so
  // answering them over the full mapping list makes both permanently true: the
  // merchant row alone reads "Linked", and `every(is_mapped === false)` can never
  // hold. Both states must be derived from the vendor entries alone.
  const getVendorStoreMappings = (product) => {
    const mappings = getStoreMappings(product);
    if (!Array.isArray(mappings)) return null;
    return mappings.filter((m) => m && m.owner_type === 'vendor');
  };

  // Human-readable platform name for the Store column.
  const PLATFORM_LABELS = {
    shopify: 'Shopify',
    easyecom: 'EasyEcom',
    easy_ecom: 'EasyEcom',
    woocommerce: 'WooCommerce',
    amazon: 'Amazon',
  };

  const getPlatformLabel = (slug) => {
    if (!slug) return null;
    return PLATFORM_LABELS[slug] || slug.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Store column — which platform(s) the product is on, or "-" when unmapped.
  const getStoreLabel = (product) => {
    const storeMappings = getStoreMappings(product);
    if (!storeMappings || storeMappings.length === 0) {
      return <span className="storeNone">-</span>;
    }

    // De-duplicate platforms so a product on two Shopify stores reads "Shopify",
    // not "Shopify, Shopify".
    const platforms = [];
    storeMappings.forEach((m) => {
      const label = getPlatformLabel(m?.platform);
      if (label && !platforms.includes(label)) platforms.push(label);
    });

    if (platforms.length === 0) return <span className="storeNone">-</span>;

    return (
      <span className="storeLabel" title={platforms.join(', ')}>
        {platforms.join(', ')}
      </span>
    );
  };

  // Linking Status column — linked / not linked.
  //
  // Linking and mapping are different questions, and this column answers the
  // linking one: is there a VENDOR store mapping row carrying an external product
  // id? That survives unmapping by design, so a product whose vendor sync was
  // switched off still reads "Linked" — but deleting the vendor connection entry
  // must flip it to "Not Linked", which is why the merchant row is excluded here.
  //
  // Mapping (sync on/off) is a vendor-store concern, so it rides along as a small
  // dot on the badge rather than taking a column of its own. The dot is always
  // rendered for a linked product — showing it only when sync is off would make a
  // bare "Linked" ambiguous (sync on, or indicator missing?), so an admin could
  // never read mapped-vs-unmapped at a glance. Green dot = syncing, hollow amber
  // dot = sync off, and the tooltip spells out the consequence either way.
  const getLinkingStatus = (product) => {
    const storeMappings = getVendorStoreMappings(product);
    const linked = Array.isArray(storeMappings)
      ? storeMappings.filter((m) => m && m.external_product_id)
      : [];

    if (linked.length === 0) {
      return (
        <span
          className="linkingStatusBadge unlinked"
          title="Not linked to the vendor store — no vendor store mapping exists for this product"
        >
          Not Linked
        </span>
      );
    }

    // Synced if the product is still mapped on at least one vendor store; a
    // product syncing somewhere isn't silently stalled.
    const isSyncing = linked.some((m) => m.is_mapped !== false);

    return (
      <span
        className="linkingStatusWrap"
        title={
          isSyncing
            ? 'Linked and syncing — updates from the vendor store are being applied'
            : 'Linked, but vendor sync is off (unmapped) — updates from the vendor store are not being applied'
        }
      >
        <span className="linkingStatusBadge linked">
          <span
            className={`syncDot ${isSyncing ? 'syncOn' : 'syncOff'}`}
            aria-hidden="true"
          />
          Linked
        </span>
        <span className="srOnly">
          {isSyncing ? ', syncing' : ', sync off'}
        </span>
      </span>
    );
  };
  
  // Selection state, relative to the current page.
  //
  // In select-all mode the selection isn't a list — it's "everything matching the
  // filters", so a row is selected unless it was explicitly excluded. Membership is
  // resolved through Sets because this runs once per row per render.
  const excludedSet = new Set(excludedIds || []);
  const selectedSet = new Set(selectedProducts || []);

  const isRowSelected = (productId) =>
    selectAllMode ? !excludedSet.has(productId) : selectedSet.has(productId);

  const pageIds = (products || []).map(p => p.id);
  const selectedOnPage = pageIds.filter(isRowSelected);
  const isAllSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
  const isPartiallySelected = selectedOnPage.length > 0 && selectedOnPage.length < pageIds.length;

  return (
    <div className={`tableContainer ${userRole === 'vendor' ? 'vendorUser' : ''}`}>
      
      {/* Table */}
      <div className="productTableWrapper">
        <table className="productTable">
          <thead>
            <tr>
              <th className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={input => {
                    if (input) {
                      input.indeterminate = isPartiallySelected;
                    }
                  }}
                  onChange={(e) => handleSelectAll(e)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={isReadOnly}
                />
              </th>
              <th>Image</th>
              <th 
                className={`sortableHeader ${sortField === 'title' ? 'activeSort' : ''}`}
                onClick={() => handleSort('title')}
              >
                Title
                {sortField === 'title' && (
                  <span className="sortIcon">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              {/* Hide Vendor Name column if user is Vendor */}
              {userRole !== 'vendor' && (
                <th 
                  className={`sortableHeader ${sortField === 'vendorName' ? 'activeSort' : ''}`}
                  onClick={() => handleSort('vendorName')}
                >
                  Vendor Name
                  {sortField === 'vendorName' && (
                    <span className="sortIcon">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
              )}
              {/* Single status column: the catalog status (draft/active/unlisted/
                  archived). The approval state used to sit beside it, but
                  `approval_status` falls through to `terminal_status` once a product
                  is approved, so the two columns rendered the same value — and the
                  approval axis is already expressed by the tabs themselves (approved
                  products in Manage Products, pending ones in the approval queue). */}
              <th
                className={`sortableHeader ${sortField === 'productStatus' ? 'activeSort' : ''}`}
                onClick={() => handleSort('productStatus')}
              >
                Status
                {sortField === 'productStatus' && (
                  <span className="sortIcon">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th
                className={`sortableHeader ${sortField === 'store' ? 'activeSort' : ''}`}
                onClick={() => handleSort('store')}
              >
                Store
                {sortField === 'store' && (
                  <span className="sortIcon">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th
                className={`sortableHeader linkingStatusHeader ${sortField === 'linkingStatus' ? 'activeSort' : ''}`}
                onClick={() => handleSort('linkingStatus')}
              >
                Linking Status
                {sortField === 'linkingStatus' && (
                  <span className="sortIcon">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th
                className={`sortableHeader ${sortField === 'updatedAt' ? 'activeSort' : ''}`}
                onClick={() => handleSort('updatedAt')}
              >
                Updated At
                {sortField === 'updatedAt' && (
                  <span className="sortIcon">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </th>
              <th className="actionsColumn"></th>
            </tr>
          </thead>
          <tbody>
      {isLoading ? (
              <tr>
                <td colSpan={userRole === 'vendor' ? '8' : '9'} className="emptyState">
                  <div className="emptyStateContent">
                    <div className="spinner"></div>
                    <p>Loading products...</p>
                  </div>
                </td>
              </tr>
            ) : error ? (
              /*
                Ahead of the empty check on purpose. "No products found" is only
                true once a request has come back; saying it when the request
                failed tells the user their catalogue is empty when we merely
                could not read it.
              */
              <DataFallback
                variant="table"
                colSpan={userRole === 'vendor' ? 8 : 9}
                error={error}
                onRetry={onRetry}
              />
            ) : products.length === 0 ? (
              <tr>
        <td colSpan={userRole === 'vendor' ? '8' : '9'} className="emptyState">
                  <div className="emptyStateContent">
                    <p>No products found</p>
                    <p className="emptyStateSubtext">
                      Try adjusting your filters or search criteria
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              products.map((product) => (
                <tr
                  key={product.id}
                  className="productRow productRowClickable"
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(product.id);
                    } else if (onProductAction) {
                      onProductAction('edit', product.id);
                    }
                  }}
                >
                  <td className="checkboxColumn" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isRowSelected(product.id)}
                      onChange={(e) => handleProductSelect(product.id, e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className="productImageCell">
                    <div className="productImageContainer">
                      {getPrimaryImageUrl(product) ? (
                        <img 
                          src={getPrimaryImageUrl(product)} 
                          alt={product.title || 'Product image'}
                          className="productImage"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="productImagePlaceholder" style={{display: getPrimaryImageUrl(product) ? 'none' : 'flex'}}>
                        <span>No Image</span>
                      </div>
                    </div>
                  </td>
                  <td className="productTitleCell">
                    <div className="productTitleRow">
                      <span className={`stateDot ${getStateDotClass(product.state)}`} title={product.state === 'active' ? 'Live on Shopify' : 'Not live'} />
                      {/* Clamped to 2 lines so rows keep a uniform height regardless
                          of title length (see .productTitle in ManageProducts.css). */}
                      <span className="productTitle" title={product.title}>{product.title}</span>
                    </div>
                  </td>
                  {/* Hide Vendor Name column if user is Vendor */}
                  {userRole !== 'vendor' && (
                    <td className="vendorCell">
                      {product.vendor?.brand_name
                        || product.vendor?.company_name
                        || product.vendorName
                        || 'N/A'}
                    </td>
                  )}
                  <td className="productStatusCell">
                    <span className={`productStatusBadge ${getProductStatusClass(product.terminal_status)}`}>
                      {product.terminal_status
                        ? product.terminal_status.charAt(0).toUpperCase() + product.terminal_status.slice(1)
                        : 'N/A'}
                    </span>
                  </td>
                  <td className="storeCell">
                    {getStoreLabel(product)}
                  </td>
                  <td className="linkingStatusCell">
                    {getLinkingStatus(product)}
                  </td>
                  <td className="updatedAtCell">
                    {formatDateTime(product.updated_at || product.updatedAt)}
                  </td>
                  <td
                    className="actionsColumn actionsCell"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RowActionsComponent
                      product={product}
                      onProductAction={onProductAction}
                      isReadOnly={isReadOnly}
                      userRole={userRole}
                      userId={userId}
                      isDropdownOpen={openDropdownId === product.id}
                      onDropdownToggle={(isOpen) => setOpenDropdownId(isOpen ? product.id : null)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProductListTable;
